import { useMemo, useCallback } from 'react';
import { useApp } from '@context/AppContext';
import { useSquiggle } from './useSquiggle';
import { captureCanvas } from '@utils/canvasSnapshot';
import { uploadGameImage, buildSubmissionPath } from '@utils/imageStorage';
import GameHUD from '../shared/GameHUD';
import RelayReactions from '../relay/RelayReactions';

const REACTION_EMOJIS = ['😂', '😍', '🔥', '👀', '🤯', '✨'];

/**
 * Handles both 'squiggling' and 'transforming' phases.
 * - Squiggling: current drawer creates a random squiggle, others wait.
 * - Transforming: transformer turns the squiggle into something, spectators react.
 *
 * Requires stageRef prop for canvas snapshot capture.
 */
export default function SquiggleActive({ stageRef }) {
  const { state } = useApp();
  const { game, user, room } = state;
  const { advanceFromSquiggle, advanceFromTransform, sendReaction } = useSquiggle();

  const isSquiggling = game.phase === 'squiggling';
  const isDrawer = game.currentDrawerId === user.id;
  const currentPair = game.pairs?.[game.currentPairIndex ?? 0];

  const squigglerName = room.members?.[currentPair?.squiggler]?.nickname || 'Unknown';
  const transformerName = room.members?.[currentPair?.transformer]?.nickname || 'Unknown';
  const drawerName = isSquiggling ? squigglerName : transformerName;

  // Is the current user the transformer during the squiggle phase? (show "You're next!")
  const isNextUp = useMemo(() => {
    if (!isSquiggling || isDrawer) return false;
    return currentPair?.transformer === user.id;
  }, [isSquiggling, isDrawer, currentPair, user.id]);

  // Pair progress
  const totalPairs = game.pairs?.length || 0;
  const currentPairNum = (game.currentPairIndex ?? 0) + 1;

  // Host handles timer expiry — captures canvas, uploads to Storage, advances
  const handleTimerExpire = useCallback(async () => {
    if (!user.isHost || !stageRef) return;

    const dataUrl = captureCanvas(stageRef);
    if (!dataUrl) return;

    const pairIndex = game.currentPairIndex ?? 0;
    const phase = isSquiggling ? 'squiggle' : 'completed';
    const path = buildSubmissionPath(state.room.code,
      `squiggle/pair${pairIndex}`, phase);
    const imageUrl = await uploadGameImage(dataUrl, path);
    if (!imageUrl) return;

    if (isSquiggling) {
      advanceFromSquiggle(imageUrl);
    } else {
      advanceFromTransform(imageUrl);
    }
  }, [user.isHost, stageRef, isSquiggling, advanceFromSquiggle, advanceFromTransform, game.currentPairIndex, state.room.code]);

  // Build HUD label and sublabel
  const hudLabel = isDrawer
    ? isSquiggling ? 'Draw a squiggle!' : 'Turn it into something!'
    : isSquiggling
      ? `${drawerName} is squiggling...`
      : `${drawerName} is transforming...`;

  const hudSublabel = `Pair ${currentPairNum} of ${totalPairs} — ${squigglerName} → ${transformerName}`;

  return (
    <>
      <GameHUD
        timerEndsAt={game.timerEndsAt}
        timerDuration={game.timerDuration}
        onTimerExpire={user.isHost ? handleTimerExpire : undefined}
        emoji="〰️"
        label={hudLabel}
        sublabel={hudSublabel}
      >
        {/* Bottom bar content */}
        {isDrawer ? (
          /* Drawer: hint text */
          <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg border border-slate-100">
            <p className="text-sm font-semibold text-charcoal font-display">
              {isSquiggling
                ? 'Go wild — random lines, curves, loops!'
                : 'What do you see in the squiggle? Make it real!'}
            </p>
          </div>
        ) : (
          /* Spectator: reaction bar (only during transform phase) */
          !isSquiggling && (
            <div className="bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2 shadow-lg border border-slate-100 flex items-center gap-1.5">
              {REACTION_EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => sendReaction(emoji)}
                  className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors text-xl active:scale-90"
                >
                  {emoji}
                </button>
              ))}
            </div>
          )
        )}
      </GameHUD>

      {/* Spectator waiting message during squiggle phase */}
      {isSquiggling && !isDrawer && !isNextUp && (
        <div className="fixed bottom-20 left-0 right-0 z-40 flex justify-center pointer-events-none">
          <div className="bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg border border-slate-100">
            <p className="text-sm text-slate-400 font-display">
              Waiting for {squigglerName} to squiggle...
            </p>
          </div>
        </div>
      )}

      {/* "You're next!" indicator for the transformer during squiggle phase */}
      {isNextUp && (
        <div className="fixed top-32 right-4 z-50 animate-bounce">
          <div className="bg-amber text-white px-4 py-2 rounded-xl shadow-lg text-sm font-semibold font-display">
            You&apos;re next — you&apos;ll transform this!
          </div>
        </div>
      )}

      {/* Floating reactions overlay (both phases) */}
      <RelayReactions />
    </>
  );
}
